import {useEffect, useRef, useState} from 'react'
import {useParams, useNavigate} from 'react-router'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import PhoneBookingSkeleton from '@/customComponents/editRideComponents/PhoneBookingSkeleton.jsx'
import DestinationSection from '@/customComponents/editRideComponents/PageModals/DestinationSection.jsx'
import SmartPickupFlow from '@/customComponents/sharedComponents/smartTime/SmartPickupFlow.jsx'
import CarSection from '@/customComponents/editRideComponents/PageModals/CarSection.jsx'
import PassengerSection from '@/customComponents/editRideComponents/PageModals/PassengerSection.jsx'
import LuggageSection from '@/customComponents/editRideComponents/PageModals/LuggageSection.jsx'
import CouponForm from '@/customComponents/editRideComponents/PageModals/CouponForm.jsx'
import SpecialRequestPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/SpecialRequestPopup.jsx'
import FlightDelayedPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/FlightDelayedPopup.jsx'
import LostBaggagePopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/LostBaggagePopup.jsx'
import AirportPickupPerksPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/AirportPickupPerksPopup.jsx'
import ComingSoonPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/ComingSoonPopup.jsx'
import SuccessPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/SuccessPopup.jsx'
import PoliciesPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/PoliciesPopup.jsx'
import RouteCard from '@/customComponents/editRideComponents/RouteCard.jsx'
import DateTimeCard from '@/customComponents/editRideComponents/DateTimeCard.jsx'
import {PassengersCard, ChildSeatsCard, LuggageItemsCard, OversizedCard} from '@/customComponents/editRideComponents/SummaryCards.jsx'
import CarDetailsCard from '@/customComponents/editRideComponents/CarDetailsCard.jsx'
import TripPriceBreakdown from '@/customComponents/editRideComponents/TripPriceBreakdown.jsx'
import AirportPickupFeeInfo from '@/customComponents/editRideComponents/PageModals/AirportPickupFeeInfo.jsx'
import SpecialRequestCard from '@/customComponents/editRideComponents/SpecialRequestCard.jsx'
import PaymentMethodsCard from '@/customComponents/editRideComponents/PaymentMethodsCard.jsx'
import ReserveRideModal from '@/customComponents/editRideComponents/PageModals/ReserveRideModal.jsx'
import ExclusivePerksBar from '@/customComponents/sharedComponents/ExclusivePerksBar.jsx'
import PopularQuestions, {FaqAccordionList} from '@/customComponents/sharedComponents/PopularQuestions.jsx'
import AirportPerksModal from '@/customComponents/sharedComponents/AirportPerksModal.jsx'
import RideBackButton from '@/customComponents/sharedComponents/RideBackButton.jsx'
import {HopnReviewsWidget} from '@/customComponents/BookRideSteps/Components/hopnReviewsWidget.jsx'
import {PrebookCompareCard} from '@/customComponents/sharedComponents/bookingPerks.jsx'
import {PrebookCompareDetail} from '@/customComponents/BookRideSteps/Components/prebookCompare.jsx'
import {PayAfterDropoffContent} from '@/customComponents/BookRideSteps/modalPages/payAfterDropoff.jsx'
import {NoSurprisePopupContent} from '@/customComponents/BookRideSteps/Components/noSurprisePopupContent.jsx'
import {CancellationModificationRefundContent} from '@/customComponents/BookRideSteps/Components/cancellationModificationRefund.jsx'
import {getFaqsForFlow, FAQ_INLINE_COUNT} from '@/constants/faqs.js'
import {POLICY_CONSTANTS} from '@/constants/booking'
import {netTotalCents, ridesharePriceCents} from '@/utils/pricing.js'
import {img} from '@/utils/pluginImg.js'
import {isNearAirport} from '@/utils/airports'
import useConfirmTrip from '@/hooks/useConfirmTrip'
import {useUser} from '@/hooks/useUser'
import {identityMissing} from '@/utils/identityGate'

/**
 * `/rides/:rideId/phone-booking-confirmation` — the confirm page as the PHONE DESK's
 * draft is handed over, with the booking flow's selling screens wrapped around it.
 *
 * ── Why it is its own page, and not a flag on RideConfirm ────────────────────────────
 *
 * A passenger who reaches /rides/:id/confirm is finishing a booking they started. This
 * one arrives cold from a link an agent read down the phone: they have not seen the car,
 * the price, or what they get for it. So the confirm stack is bracketed by the three
 * screens the self-serve flow shows BEFORE payment —
 *
 *   1. the ride card: vehicle, price, tip badge, discount, and the three perk rows
 *      (selectedCarDetailsLatest)
 *   2. reviews, the prebook-vs-ride-share comparison, and the airport perks bar (same)
 *   3. the Reserve Ride footer and its trust strip (paymentCards)
 *
 * — and RideConfirm keeps doing its one job. The two share `useConfirmTrip` and every
 * card, so a change to a section lands on both.
 *
 * ── NOT LINKED, and DRAFTS ONLY ─────────────────────────────────────────────────────
 *
 * Nothing in the dashboard routes here; the URL is the entry point, by request. And the
 * page refuses anything that isn't a Draft rather than rendering a confirm flow over a
 * ride that is already confirmed, cancelled or completed — `doConfirm` would be
 * meaningless there, and the price panel would be quoting a fare that has already been
 * charged.
 */

/**
 * One row of the price panel's perks list.
 *
 * COPIED from selectedCarDetailsLatest's own `PerkRow` — same element tree, same class
 * names (`.hopn-ride-perk` / `.ic` / `.txt`, chevron inside the <strong>). It is a local
 * function there rather than an export, and the first cut of this page paraphrased it
 * with invented class names: the stylesheet matched none of them, so the rows rendered
 * as unstyled text overlapping their own icons. If that component changes, re-copy.
 */
function PerkRow({icon, title, subtitle, onOpen}) {
    return (
        <div className="hopn-ride-perk">
            <button type="button" onClick={onOpen}>
                <span className="ic"><img src={img(icon)} alt=""/></span>
                <span className="txt">
                    <strong>{title} <img className="chev" src={img('arrow-down.svg')} alt=""/></strong>
                    <small>{subtitle}</small>
                </span>
            </button>
        </div>
    )
}

export default function PhoneBookingConfirmation() {
    const {rideId} = useParams()
    const navigate = useNavigate()
    const e = useConfirmTrip(rideId)

    const isDraft = e.trip?.status === 'Draft'

    /*
     * ── Reserve Ride opens a modal, not a form ──────────────────────────────────────
     *
     * Profile (only while name / email / phone is missing), card, then the processing view
     * that books the ride — see ReserveRideModal. What the account still owes is known UP
     * FRONT from `useUser`, with the refused confirm's own list merged in as the safety net.
     *
     * `reserveKey` re-mounts the modal on every open so its step list is rebuilt from what
     * is missing NOW.
     */
    const {data: account} = useUser()
    const missingIdentity = [...new Set([
        ...identityMissing(account),
        ...(Array.isArray(e.identityMissing) ? e.identityMissing : []),
    ])]
    // A passenger with saved cards picks one on the page itself, right above the comparison;
    // the Reserve modal then skips its Secure Payment (add-card) step. No cards → that step
    // is how they add the first one.
    const savedCards = e.savedCards?.length ? e.savedCards : (e.trip?.passenger?.payment_methods || [])
    const hasSavedCards = savedCards.length > 0
    // Nothing chosen yet → the first card is, so Reserve Ride is never waiting on a pick the
    // passenger didn't know they had to make. They can still change it (a tap is saved to the draft).
    // The auto-pick itself is local state only — it is a default, not a choice.
    const firstCardId = savedCards[0]?.id
    useEffect(() => {
        if (firstCardId != null && !e.selectedCardId) e.setSelectedCardId(firstCardId)
    }, [firstCardId, e.selectedCardId]) // eslint-disable-line react-hooks/exhaustive-deps
    // Footer toggles (Spacial Request / Apply Coupon) — the ticks just mirror whether one is set.
    const [showCoupon, setShowCoupon] = useState(false)
    const hasSpecialRequest = !!(e.specialRequest?.notes || e.specialRequest?.caringPet || e.specialRequest?.quietRide)
    const hasCoupon = !!e.couponCode
    // Is the Reserve bar pinned to the screen edge (shadow) or back at its place in the page
    // (no shadow)? A zero-height marker sits where the bar naturally belongs; the bar is
    // pinned exactly while that marker is still below the bottom of the viewport.
    // ── Mobile gate nudge ──────────────────────────────────────────────────────────
    // On a phone the Reserve button's tooltip is replaced by this: tapping it while something
    // is missing scrolls to that section and prints the reason under it, with the section's
    // bottom edge resting right against the top of the pinned footer.
    const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches)
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)')
        const sync = () => setIsMobile(mq.matches)
        mq.addEventListener('change', sync)
        return () => mq.removeEventListener('change', sync)
    }, [])
    const footerRef = useRef(null)
    const gateErrRef = useRef(null)
    const [gateNudge, setGateNudge] = useState(null) // {section, message, id}
    // The button greys out for a moment after a refused tap — while the page scrolls to the
    // missing field — then goes back to black.
    const [nudging, setNudging] = useState(false)
    useEffect(() => {
        if (!gateNudge) return undefined
        const t = setTimeout(() => setNudging(false), 1500)
        return () => clearTimeout(t)
    }, [gateNudge?.id]) // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!gateNudge) return undefined
        const raf = requestAnimationFrame(() => {
            const err = gateErrRef.current
            const footer = footerRef.current
            if (!err || !footer) return
            const delta = err.getBoundingClientRect().bottom - footer.getBoundingClientRect().top + 8
            window.scrollBy({top: delta, behavior: 'smooth'})
        })
        return () => cancelAnimationFrame(raf)
    }, [gateNudge?.id]) // eslint-disable-line react-hooks/exhaustive-deps
    const [barMarker, setBarMarker] = useState(null)
    const [barPinned, setBarPinned] = useState(false)
    useEffect(() => {
        if (!barMarker) return undefined
        // -170px ≈ the bar's own height plus its top margin: the marker only counts as "in
        // view" once the whole bar would fit above the screen edge, i.e. once it has landed.
        const io = new IntersectionObserver(([entry]) => {
            setBarPinned(!entry.isIntersecting && entry.boundingClientRect.top > 0)
        }, {rootMargin: '0px 0px -170px 0px'})
        io.observe(barMarker)
        return () => io.disconnect()
    }, [barMarker])
    const [reserveOpen, setReserveOpen] = useState(false)
    const [reserveKey, setReserveKey] = useState(0)
    const onReserve = () => {
        setReserveKey((k) => k + 1)
        setReserveOpen(true)
    }
    const [showPerksModal, setShowPerksModal] = useState(false)
    // The compare card's PHONE view: the booking flow's `prebookCompare` step, as a modal here
    // (this page has no step stack). Desktop keeps the shared card's own popup.
    const [showCompareStep, setShowCompareStep] = useState(false)
    const [showMoreFaq, setShowMoreFaq] = useState(false)
    const [showPoliciesPopup, setShowPoliciesPopup] = useState(false)
    // 'payAfter' | 'cancellation' | 'flatRate' — the price panel's three perk rows.
    const [perkPopup, setPerkPopup] = useState(null)
    // The ⓘ beside the struck-through price: the line-by-line breakdown behind it.
    // `pricePopupFee` swaps that popup to the airport-fee explainer and back, rather than
    // stacking a second Modal on top of the first — same two-view shape the booking
    // flow's own price popup uses.
    const [showPricePopup, setShowPricePopup] = useState(false)
    const [pricePopupFee, setPricePopupFee] = useState(false)

    /*
     * ── ONE loading state ───────────────────────────────────────────────────────────
     *
     * The page needs TWO requests before it can be drawn: the trip, and then the fare it
     * quotes for itself (a Draft carries none — see the price note below). The first cut
     * showed the skeleton for the trip and then a spinner inside the ride card for the
     * fare, which read as the page loading twice.
     *
     * So the skeleton simply stays up until BOTH have landed. `everReady` is what stops
     * it coming back: `isRecalculating` goes true again on every re-quote — a changed
     * date, car or tip — and gating the page on that would blow the whole layout away
     * mid-edit. Those re-quotes are signalled where they belong, in CarDetailsCard's own
     * inline loader.
     */
    const [everReady, setEverReady] = useState(false)
    const vehicle = e.trip?.selected_vehicle
    const quoteReady = !!vehicle && !e.isRecalculating

    useEffect(() => {
        if (quoteReady) setEverReady(true)
    }, [quoteReady])

    const faqs = getFaqsForFlow(e.tripDirection, {
        freeCancellationAvailable: POLICY_CONSTANTS.FREE_CANCELLATION_AVAILABLE,
        freeCancellationHours: POLICY_CONSTANTS.FREE_CANCELLATION_HOURS,
        freeEditsCount: POLICY_CONSTANTS.FREE_EDITS_COUNT,
        editableHoursBeforePickup: POLICY_CONSTANTS.EDITABLE_HOURS_BEFORE_PICKUP,
        waitingPickupFreeSeconds: POLICY_CONSTANTS.WAITING_PICKUP_FREE_SECONDS,
        waitingPickupRateCentsPerSecond: POLICY_CONSTANTS.WAITING_PICKUP_RATE_CENTS_PER_SECOND,
        waitingPickupMaxWaitSeconds: POLICY_CONSTANTS.WAITING_PICKUP_MAX_WAIT_SECONDS,
        cancellationText: POLICY_CONSTANTS.CANCELLATION_TEXT,
        modificationText: POLICY_CONSTANTS.MODIFICATION_TEXT,
    })

    useEffect(() => {
        document.body.classList.add('ride-edit-page')
        return () => document.body.classList.remove('ride-edit-page')
    }, [])

    const renderSection = () => {
        switch (e.editingSection) {
            case 'destination': return <DestinationSection mode={e.destinationPart || 'all'} destinations={e.destinations} committedDestinations={e.committedDestinations} setDestinations={e.handleDestinationsChange} onDone={() => e.handleSectionDone('destination')} onClose={e.closeEdit} isLoading={e.isRecalculating} error={e.sectionSaveError}/>
            case 'car': return <CarSection availableCars={e.availableCars} selectedCar={e.selectedCar} committedCarTypeId={e.committedCarTypeId} setSelectedCar={(car) => { e.setSelectedCar(car) }} onDone={() => e.handleSectionDone('car')} onClose={e.closeEdit} isLoading={e.isRecalculating} isRecalculating={e.isRecalculating} error={e.sectionSaveError} driverTip={e.driverTip} setDriverTip={e.setDriverTip} onTipChange={e.handleTipChange} onCouponRecalculate={e.handleCouponRecalculate} defaultCouponCode={e.couponCode} breakdownCarId={e.carBreakdownId} setBreakdownCarId={e.setCarBreakdownId}/>
            case 'passengers': return <PassengerSection tripId={rideId} passengers={e.passengers} committedPassengers={e.committedPassengers} setPassengers={e.handlePassengersChange} childSeats={e.childSeats} committedChildSeats={e.committedChildSeats} setChildSeats={e.handleChildSeatsChange} passengerTypes={e.passengerTypes} childSeatTypes={e.childSeatTypes} onDone={() => e.handleSectionDone('passengers')} onClose={e.closeEdit} isLoading={e.isRecalculating} error={e.sectionSaveError} view={e.passengerView} onViewChange={e.setPassengerView} only={e.passengerOnly}/>
            case 'luggage': return <LuggageSection tripId={rideId} luggage={e.luggage} committedLuggage={e.committedLuggage} setLuggage={e.handleLuggageChange} oversizedLuggage={e.oversizedLuggage} committedOversizedLuggage={e.committedOversizedLuggage} setOversizedLuggage={e.handleOversizedChange} luggageTypes={e.luggageTypes} onDone={() => e.handleSectionDone('luggage')} onClose={e.closeEdit} isLoading={e.isRecalculating} error={e.sectionSaveError} oversizedMode={e.luggageOversizedMode} setOversizedMode={e.setLuggageOversizedMode}/>
            default: return null
        }
    }

    if (e.isLoading) {
        return <div className="py-5 pb-10 max-md:bg-(--bg-grey)"><PhoneBookingSkeleton/></div>
    }

    if (!e.trip) {
        return <div className="py-5 pb-10"><div className="max-w-3xl mx-auto text-center p-4"><p className="text-(--text-secondary)">Trip not found</p></div></div>
    }

    // The gate. Anything already confirmed, cancelled or completed has nothing here to
    // confirm — `doConfirm` would be meaningless and the price panel would be quoting a
    // fare that has already been charged.
    //
    // It used to say so in a bare "This page only handles draft bookings" panel. It now
    // shows the SAME booking-received card deck the confirm pages show after a successful
    // submit (SuccessPopup → ConfirmBookingReceivedTabs): a passenger who opens their
    // handoff link a second time, after confirming, gets the perks / return / next-steps
    // screen for the booking they already made instead of an error about drafts. Same
    // component, same three tabs — the only difference is that Done sends them to the
    // trip rather than home, because they arrived AT this trip.
    // `!reserveOpen`: a successful confirm seats the confirmed trip (no longer a Draft) straight
    // away. Without this the page would swap to the booking-received deck at that instant and
    // unmount the Reserve modal mid-processing — skipping the map / minimum wait. The modal
    // closes itself when the wait is over, and this branch takes over then.
    if (!isDraft && !reserveOpen) {
        return (
            <div className="py-5 pb-10 max-md:bg-(--bg-grey)">
                <SuccessPopup isOpen trip={e.trip} isReturnFlow={e.trip?.trip_type === 'ReturnBooking'}
                              onDone={() => navigate(`/rides/${rideId}`)}/>
            </div>
        )
    }

    /*
     * Still quoting the fare. The SAME skeleton, held a little longer — see `everReady`.
     *
     * ⚠️ `selected_vehicle` IS ABSENT ON A DRAFT: the server holds no fare for one, so
     * this page runs calculate-route → calculate-cost itself and the answer lands a beat
     * after `isLoading` has already gone false. Drawing the page in that beat is what put
     * "$0", "~$0" and a nameless vehicle on screen — netTotalCents falls back to
     * `total_price ?? 0` with no breakdown to read, so the zeros looked like real values.
     */
    if (!everReady) {
        return <div className="py-5 pb-10 max-md:bg-(--bg-grey)"><PhoneBookingSkeleton/></div>
    }

    /*
     * FINAL price, not the pre-tax one.
     *
     * `netTotalCents` is the booking flow's ADVERTISED price — the breakdown's pre-tax
     * `net_total` row — and every screen in that flow quotes it. This page is the last
     * thing a caller sees before they commit, so it shows what will actually be charged:
     * `total_price`, the same figure CarDetailsCard labels "Total" further down. Two
     * different numbers for one trip on one page is the thing to avoid here.
     *
     * The tax that separates the two is then added to the OTHER two figures as well, so
     * all three stay on one basis: a tax-inclusive $141 sitting next to a pre-tax
     * ride-share estimate would flatter us by exactly the tax, and a pre-tax "was" price
     * would make the discount look smaller than it is.
     */
    const netCents = netTotalCents(vehicle)
    const finalCents = vehicle?.total_price ?? netCents
    const taxCents = Math.max(0, finalCents - netCents)

    const price = Math.ceil(finalCents / 100)
    // Never under our own price — the two roundings differ (see comparePrices).
    const sharePrice = Math.max(price, Math.round((ridesharePriceCents(vehicle) + taxCents) / 100))

    const pricing = vehicle?.pricing || {}
    const oldPrice = pricing.total_before_discounts
        ? Math.ceil((pricing.total_before_discounts + taxCents) / 100)
        : null
    const discountPercent = pricing.online_discount_percentage || 0
    const cancelHrs = POLICY_CONSTANTS.FREE_CANCELLATION_HOURS

    const shortLabel = (loc) => String(loc?.address || '').split(',')[0].trim()
    // Mobile compare header: airport code › city › first address chunk (as the booking flow's compare row).
    const compactLabel = (loc) => {
        if (!loc) return ''
        const near = loc.lat && loc.lng ? isNearAirport(loc.lat, loc.lng) : false
        if (near && near.code) return near.code
        const code = loc.address?.match(/\(([A-Z]{3})\)/)
        if (code) return code[1]
        if (loc.city) return loc.city
        return shortLabel(loc)
    }
    const pickupLabel = shortLabel(e.routeLocations?.origin || e.committedDestinations?.origin)
    const dropoffLabel = shortLabel(e.routeLocations?.destination || e.committedDestinations?.destination)
    const pickupCompact = compactLabel(e.routeLocations?.origin || e.committedDestinations?.origin)
    const dropoffCompact = compactLabel(e.routeLocations?.destination || e.committedDestinations?.destination)

    // The same gate RideConfirm's Continue carries — here it sits on Reserve Ride, which
    // is this page's only submit.
    // Payment is collected in the step after Reserve Ride, so it is not gated here.
    const hasDate = !!e.liveDatetime?.date || !!e.trip?.scheduled_at
    const hasTime = !!e.liveDatetime?.time || !!e.trip?.scheduled_at
    const gateMessage = !hasDate ? 'Please select the arrival date to continue'
        : (e.isFromAirport && !e.hasFlightNumber) ? 'Please enter your flight number to continue'
        : !hasTime ? 'Please select the arrival time to continue'
        : (hasSavedCards && !e.selectedCardId) ? 'Please select a payment method to continue'
        : null
    // Which field the message is about — the mobile nudge scrolls to it and prints under it.
    const gateField = !hasDate ? 'date'
        : (e.isFromAirport && !e.hasFlightNumber) ? 'flight'
        : !hasTime ? 'time'
        : null
    const gateSection = gateField ? 'datetime' : (hasSavedCards && !e.selectedCardId) ? 'payment' : null
    // The nudge only lives while the thing it complains about is still missing.
    const nudgeError = gateNudge && gateMessage && gateNudge.message === gateMessage ? gateNudge : null
    const onReserveClick = () => {
        if (gateMessage) {
            if (isMobile) {
                setNudging(true)
                setGateNudge({section: gateSection, field: gateField, message: gateMessage, id: Date.now()})
            }
            return
        }
        onReserve()
    }

    return (
        <div className="py-5 pb-10 max-md:bg-(--bg-grey)">
            {/* ONE vertical rhythm down the page: every block below is separated by 28px
                (mb-7 / gap-7), because the reused cards each ship a margin of their own
                and the page was stacking them on top of the gap — the reviews card's
                30px, the perks bar's 16px top and the FAQ card's 16px bottom made three
                different, visibly larger holes. Those margins are zeroed where they are
                stacked (see the section below); the gap is the only spacing. */}
            <div className="max-w-3xl mx-auto text-center mb-7 relative px-14">
                <RideBackButton className="absolute left-2 top-1/2 -translate-y-1/2"/>
                <h1 className="font-bold text-2xl mb-0!">Confirm Booking</h1>
            </div>

            {/* ── 1. The ride card ────────────────────────────────────────────────────
                What the caller was quoted: the vehicle, the price with its tip badge and
                discount, and the three things that price includes. */}
            <div className="max-w-3xl mx-auto mb-7 hopn-ride-latest">
                <div className="hopn-ride-card">
                    <div className="hopn-ride-card-top">
                        <div className="hopn-ride-card-info">
                            <h3>
                                {vehicle?.vehicle_name || 'Vehicle'}
                                {(vehicle?.vehicles_needed || 1) > 1 && ` X${vehicle.vehicles_needed}`}
                                {vehicle?.vehicle_reference && <span className="text-inherit!"> {vehicle.vehicle_reference}</span>}
                            </h3>
                            <p>{vehicle?.vehicle_details || ''}</p>
                            {vehicle?.vehicle_image && (
                                <img className="hopn-ride-card-car" src={vehicle.vehicle_image} alt={vehicle.vehicle_name || ''}/>
                            )}
                        </div>

                        <div className="hopn-ride-price-box hopn-ride-price-box--centered">
                            <div className="hopn-ride-price-box-inner">
                                <div className="hopn-ride-price-main max-md:justify-start!">
                                    <span className="now">${price}</span>
                                    {oldPrice && discountPercent > 0 && (
                                        <div className="hopn-ride-price-main-inner">
                                            <span className="was">
                                                <s>${oldPrice}</s>
                                                {/* The booking flow puts the breakdown behind this ⓘ
                                                    (selectedCarDetailsLatest); the page was missing it
                                                    entirely, so the struck price had nothing explaining
                                                    the gap between it and the one being charged. */}
                                                <button
                                                    type="button"
                                                    className="hopn-ride-price-info"
                                                    onClick={() => { setPricePopupFee(false); setShowPricePopup(true) }}
                                                    aria-label="Price breakdown"
                                                >
                                                    <img src={img('info-icon-big.svg')} alt="Info"/>
                                                </button>
                                            </span>
                                            <span className="hopn-ride-save">Save {discountPercent}% Off</span>
                                        </div>
                                    )}
                                </div>
                                {/* After the price, not above it — reads "$129 · Tip included". */}
                                <span className="hopn-ride-tip-badge hopn-ride-tip-badge--after">
                                    Tip included <img src={img('circle-check-mark.svg')} alt=""/>
                                </span>
                            </div>

                            <div className="hopn-ride-perks">
                                <PerkRow icon="square-big-fav.svg" title="Pay after drop off"
                                         subtitle="Square hold funds" onOpen={() => setPerkPopup('payAfter')}/>
                                <PerkRow icon="cancellation-changes.svg" title="Cancellation & Changes"
                                         subtitle={cancelHrs > 0 ? `Up ${cancelHrs} hrs before pickup` : 'According to the policies'}
                                         onOpen={() => setPerkPopup('cancellation')}/>
                                <PerkRow icon="lock.svg" title="Guaranteed flat rate"
                                         subtitle="No surge, km, or minute charges"
                                         onOpen={() => setPerkPopup('flatRate')}/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <>
            {/* ── 2. Reviews, the comparison, the perks bar ───────────────────────────
                Why this price is worth paying, before the form asks for the details. */}
            <div className="max-w-3xl mx-auto mt-7 mb-7 flex flex-col gap-7 hopn-ride-latest
                            [&_.hopn-reviews-widget--collapsible]:mb-0!
                            max-md:[&_.hopn-reviews-widget--collapsible]:bg-white!
                            [&_.pc-more-bar]:mt-0!
                            [&_.hopn-ride-faq]:mb-0!">
                <HopnReviewsWidget layout="collapsible"/>

                <PopularQuestions faqs={faqs} onMore={() => setShowMoreFaq(true)} hideHead/>
            </div>

            {/* ── 3. Trip details — the confirm stack's cards, inside the same collapse ── */}
            <div className="relative max-w-3xl mx-auto flex flex-col gap-4 p-5 pb-8 bg-(--bg-grey) rounded-(--radius-2xl) max-md:p-0 max-md:rounded-none max-md:bg-transparent">
                <RouteCard title={e.isFromAirport ? 'From Airport' : 'Start Ride Address'} summary="origin" showBanner={false} includeStopovers={e.isToAirport}
                           trip={{...e.trip, ...e.routeLocations, route: e.route, scheduled_at: e.scheduledAt}}
                           expanded={e.expanded.has('route')} onToggle={() => e.toggle('route')} onEdit={() => e.openEdit('destination', {part: e.isToAirport ? 'originStops' : 'origin'})}/>

                <RouteCard title="Drop off Address" summary="destination" showBanner={false} includeStopovers={!e.isToAirport}
                           trip={{...e.trip, ...e.routeLocations, route: e.route, scheduled_at: e.scheduledAt}}
                           expanded={e.expanded.has('dropoff')} onToggle={() => e.toggle('dropoff')} onEdit={() => e.openEdit('destination', {part: e.isToAirport ? 'destinationOnly' : 'dropoff'})}/>

                <DateTimeCard
                    trip={e.trip} parentScheduledAt={e.trip?.parent_trip?.scheduled_at}
                    isAirportTrip={e.isAirportTrip} hideFlightField={e.isToAirport}
                    isFromAirport={e.isFromAirport} isToAirport={e.isToAirport}
                    flightNumber={e.sharedFlightNumber}
                    onFlightNumberChange={e.handleFlightNumberChange}
                    onFlightClear={e.clearFlight} flightAdded={e.hasFlightNumber}
                    onSmartTime={e.handleFlightAdd}
                    applySmartTimeRef={e.applySmartTimeRef}
                    onDateTimeChange={e.setLiveDatetime} onDateChange={e.handleDateChange} onTimeChange={e.handleTimeChange}
                    dateHint={e.dateHint} setDateHint={(v) => { e.setDateHint(v); if (!v && e.isFromAirport) e.setFlightHint(true) }}
                    dateHintText="Please set the date"
                    dateTimeSectionRef={e.dateTimeSectionRef}
                    leadTimeError={e.minLeadTimeError}
                    saving={e.isSavingDateTime}
                    gateError={nudgeError?.field ? nudgeError : null} gateErrorRef={gateErrRef}
                />

                <PassengersCard trip={e.trip} expanded={e.expanded.has('passengers')} onToggle={() => e.toggle('passengers')} onEdit={() => e.openEdit('passengers', {passengerOnly: 'passengers'})} policy={e.getFieldPolicy('passenger_types')}/>

                <ChildSeatsCard trip={e.trip} expanded={e.expanded.has('childseats')} onToggle={() => e.toggle('childseats')} onEdit={() => e.openEdit('passengers', {passengerOnly: 'childseats'})} policy={e.getFieldPolicy('child_seats')}/>

                <LuggageItemsCard trip={e.trip} expanded={e.expanded.has('luggage')} onToggle={() => e.toggle('luggage')} onEdit={() => e.openEdit('luggage')} policy={e.getSectionPolicy('luggage')}/>

                <OversizedCard trip={e.trip} expanded={e.expanded.has('oversized')} onToggle={() => e.toggle('oversized')} onEdit={() => e.openEdit('luggage', {oversized: true})} policy={e.getSectionPolicy('luggage')}/>

                <div className="flex gap-4 items-stretch max-md:flex-col">
                    <SpecialRequestCard trip={e.trip} specialRequest={e.specialRequest} contact={e.contact} onEditSpecialRequest={() => e.setShowSRPopup(true)}/>
                    <CarDetailsCard trip={e.trip} driverTip={e.driverTip} setDriverTip={e.setDriverTip} onTipChange={e.handleTipChange} isRecalculating={e.isRecalculating} onEditCar={() => e.openEdit('car')}/>
                </div>
            </div>
            </>

            {/* ── Why this price — the Hopn-vs-ride-share comparison and the airport perks bar,
                right after the card is chosen and just before the commit. ──────────────── */}
            <div className="max-w-3xl mx-auto mt-7 flex flex-col gap-7 hopn-ride-latest
                            max-md:[&_.hopn-ride-compare]:bg-white!
                            [&_.pc-more-bar]:mt-0!">
                {hasSavedCards && (
                    <div className="bg-(--bg-grey) rounded-(--radius-2xl) p-4 max-md:p-0 max-md:bg-transparent max-md:rounded-none">
                        <PaymentMethodsCard trip={e.trip} savedCards={e.savedCards} selectedCardId={e.selectedCardId}
                                            setSelectedCardId={e.handleCardSelect}
                                            onCardAdded={(c) => e.setSavedCards((prev) => [...prev, c])}
                                            walletAmountCents={e.trip?.wallet_amount_cents}/>
                    </div>
                )}
                {hasSavedCards && nudgeError?.section === 'payment' && (
                    <p ref={gateErrRef} className="md:hidden text-red-500 text-sm font-semibold mb-0! px-1">{nudgeError.message}</p>
                )}
                {/* The shared compare card + its popup (same component the booking flow and the
                    booking-received tabs use) — `forcePopup` because this page has no step
                    stack, `portal` so the popup is a real viewport modal on a phone. */}
                <PrebookCompareCard price={price} sharePrice={sharePrice}
                                    pickup={isMobile ? pickupCompact : pickupLabel}
                                    dropoff={isMobile ? dropoffCompact : dropoffLabel}
                                    setStep={(step) => { if (step === 'prebookCompare') setShowCompareStep(true) }}
                                    spacing={false} portal/>

                <div className="prebook-compare">
                    <ExclusivePerksBar onClick={() => setShowPerksModal(true)}/>
                </div>
            </div>

            {/* ── 4. Reserve Ride + trust strip ───────────────────────────────────────
                OUTSIDE the grey panel above. That panel is the FORM — the things still
                being filled in — and the commit is not one of them; sitting inside, the
                button read as one more field rather than as the end of the page.

                This is also the page's ONLY submit. RideConfirm's plain "Continue" is not
                rendered as well — two buttons calling `doConfirm` would leave one of them
                looking dead — so the gate and its bubble moved onto this one. */}
            {/* `contents`: this wrapper adds no box of its own, so the footer below is a direct child
                of the page's tall root — a sticky element only pins within its PARENT, and the
                old wrapper was just the height of the footer itself, so nothing ever stuck. */}
            <div className="contents">
                {/* mb-0!: `.hopn-pay-footer` ships `margin: 22px 0 30px` for the booking
                    flow, where it is the last thing on the screen. Here the terms line
                    follows it, so that 30px stacked on the column's own gap and left a
                    hole — the gap alone is the spacing. */}
                {/* Pinned to the bottom of the viewport while the page scrolls under it (it settles
                    into place at the end of the page). `mt-7! mb-0!`: the footer's own 22px/30px
                    margins are for the flow it was built in, not for a pinned bar. */}
                <div ref={setBarMarker} aria-hidden="true" className="h-0"/>
                <div ref={footerRef} className={`hopn-pay-footer max-w-3xl mx-auto mt-7! mb-0! gap-3! sticky bottom-0 z-20 bg-white rounded-t-3xl max-md:rounded-b-3xl px-4 pt-4 pb-4 transition-shadow ${barPinned ? 'shadow-[0_-6px_24px_rgba(0,0,0,0.08)]' : ''}`}>
                    <div className="hopn-ride-actions is-guest mb-0!">
                        <div className="hopn-ride-actions-toggles">
                            <div className="hopn-ride-action">
                                <span className="lbl" onClick={() => e.setShowSRPopup(true)}><div className="icon"><img src={img('dog-foot-print.svg')} alt=""/></div> Spacial Request</span>
                                <input type="checkbox" id="pbc-special-request" className="hopn-checkbox"
                                       checked={hasSpecialRequest} onChange={() => e.setShowSRPopup(true)}/>
                                <label htmlFor="pbc-special-request" className="hopn-checkbox-toggle"></label>
                            </div>
                            <div className="hopn-ride-action">
                                <span className="lbl" onClick={() => setShowCoupon(true)}><div className="icon"><img src={img('coupon.svg')} alt=""/></div> {hasCoupon ? 'Update Coupon' : 'Apply Coupon'}</span>
                                <input type="checkbox" id="pbc-coupon" className="hopn-checkbox"
                                       checked={hasCoupon} onChange={() => setShowCoupon(true)}/>
                                <label htmlFor="pbc-coupon" className="hopn-checkbox-toggle"></label>
                            </div>
                        </div>
                    </div>
                    {/* The bubble is anchored to THIS wrapper, not to .hopn-pay-footer — the
                        footer also holds the trust strip, so `top-full` measured from it put
                        the bubble below BOTH and straight over the terms line. And it sits
                        ABOVE the button (bottom-full, arrow pointing down) because below it
                        there is nothing but the trust strip to cover. */}
                    <div className="relative">
                        <button
                            type="button"
                            className={`hopn-reserve-ride-btn ${nudging ? 'opacity-60 pointer-events-none' : ''}`}
                            onClick={onReserveClick}
                            disabled={e.isSubmitting || (!!gateMessage && !isMobile) || !!e.minLeadTimeError}
                        >
                            <span className="hopn-reserve-ride-inner">
                                <span className="hopn-reserve-ride-title">
                                    <img src={img('lock.svg')} alt=""/>
                                    Reserve Ride
                                </span>
                                <small>You'll be charged after your drop-off.</small>
                            </span>
                        </button>

                        {gateMessage && (
                            <div className="max-md:hidden absolute bottom-full right-0 mb-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-t-20 after:border-t-[#3c393c] after:-bottom-4.75 after:right-7.5">
                                <p className="mb-0!">{gateMessage}</p>
                            </div>
                        )}
                    </div>
                </div>

                <p className="max-w-3xl mx-auto mt-6 mb-0 px-4 text-sm text-center cursor-pointer underline" onClick={() => setShowPoliciesPopup(true)}>By proceeding, you confirm that you understand and agree to these terms.</p>
            </div>

            {/* ── Popups, all shared with RideConfirm ─────────────────────────────── */}
            <Modal isOpen={e.smartTimePopup} onClose={e.closeSmartTime} className="md:max-w-157.5" flushBody flushTop>
                <SmartPickupFlow
                    destinations={[
                        e.committedDestinations?.origin,
                        e.committedDestinations?.destination,
                        ...(e.committedDestinations?.stopOvers || []),
                    ].filter(Boolean)}
                    rideDate={e.liveDatetime.date}
                    routeData={e.route}
                    flightNumber={e.sharedFlightNumber}
                    flightDetails={e.sharedFlightDetails}
                    onFlightChange={({flightNumber, flightDetails}) => {
                        e.setSharedFlightNumber(flightNumber);
                        e.setSharedFlightDetails(flightDetails);
                    }}
                    onSelectTime={e.handleSmartTimeSelect}
                    onClose={e.closeSmartTime}
                    hideBack
                />
            </Modal>

            <Modal isOpen={!!e.editingSection} onClose={e.closeEdit}>
                <ModalHeader
                    title={
                        e.passengerView === 'seatDetails' && e.editingSection === 'passengers' ? 'Found Right Seat'
                        : e.luggageOversizedMode && e.editingSection === 'luggage' ? 'Add Oversized'
                        : e.carBreakdownId && e.editingSection === 'car' ? 'Payment'
                        : e.editingSection === 'destination' ? ((e.destinationPart === 'origin' || e.destinationPart === 'originStops') ? (e.isFromAirport ? 'From Airport' : 'Start Ride Address') : (e.destinationPart === 'dropoff' || e.destinationPart === 'destinationOnly') ? 'Drop off Address' : 'Pick-up & Drop-off')
                        : e.editingSection === 'car' ? 'Select Vehicle'
                        : e.editingSection === 'passengers' ? (e.passengerOnly === 'childseats' ? 'Child Seats' : e.passengerOnly === 'passengers' ? 'Passengers' : 'Passengers & Child Seats')
                        : e.editingSection === 'luggage' ? 'Luggage'
                        : ''
                    }
                    onBack={
                        (e.passengerView === 'seatDetails' && e.editingSection === 'passengers') ? () => e.setPassengerView('main')
                        : (e.carBreakdownId && e.editingSection === 'car') ? () => e.setCarBreakdownId(null)
                        : undefined
                    }
                    onClose={(e.carBreakdownId && e.editingSection === 'car') ? undefined : e.closeEdit}
                />
                {renderSection()}
            </Modal>

            {/* The ⓘ beside the struck price. Two views in ONE modal — Payment, and the
                airport-fee explainer reached from the fee row — because a Modal opened
                over a Modal loses the first one's scroll position on the way back. */}
            <Modal isOpen={showPricePopup} onClose={() => { setShowPricePopup(false); setPricePopupFee(false) }}>
                <ModalHeader
                    title={pricePopupFee ? 'Airport Pickup Fee' : 'Payment'}
                    onBack={pricePopupFee ? () => setPricePopupFee(false) : undefined}
                    onClose={() => { setShowPricePopup(false); setPricePopupFee(false) }}
                />
                {pricePopupFee ? <AirportPickupFeeInfo/> : (
                    <TripPriceBreakdown trip={e.trip} gratuityPercent={e.driverTip}
                                        onAirportFeeClick={() => setPricePopupFee(true)}/>
                )}
            </Modal>

            {/* The price panel's three perk rows, each opening the booking flow's own copy. */}
            <Modal isOpen={perkPopup === 'payAfter'} onClose={() => setPerkPopup(null)}>
                <ModalHeader title="" onClose={() => setPerkPopup(null)}/>
                <PayAfterDropoffContent/>
            </Modal>

            <Modal isOpen={perkPopup === 'flatRate'} onClose={() => setPerkPopup(null)}>
                <ModalHeader title="No Surprises" onClose={() => setPerkPopup(null)}/>
                <NoSurprisePopupContent/>
            </Modal>

            <Modal isOpen={perkPopup === 'cancellation'} onClose={() => setPerkPopup(null)}>
                <ModalHeader title="" onClose={() => setPerkPopup(null)}/>
                <CancellationModificationRefundContent/>
            </Modal>

            <Modal isOpen={showCoupon} onClose={() => setShowCoupon(false)}>
                <ModalHeader title="Add A Coupon" onClose={() => setShowCoupon(false)}/>
                <CouponForm carDetails={vehicle} setApplyCoupon={setShowCoupon}
                            onRecalculate={e.handleCouponRecalculate} defaultCouponCode={e.couponCode}/>
            </Modal>
            <SpecialRequestPopup isOpen={e.showSRPopup} onClose={() => e.setShowSRPopup(false)} specialRequest={e.specialRequest} onSave={e.handleSpecialRequestSave}/>
            <FlightDelayedPopup isOpen={e.infoPopup === 'flightDelayed'} onClose={() => e.setInfoPopup(null)}/>
            <LostBaggagePopup isOpen={e.infoPopup === 'lostBaggage'} onClose={() => e.setInfoPopup(null)}/>
            <AirportPickupPerksPopup isOpen={e.infoPopup === 'morePerks'} onClose={() => e.setInfoPopup(null)}/>
            <ComingSoonPopup isOpen={e.infoPopup === 'findDriver'} onClose={() => e.setInfoPopup(null)} title="Find Your Driver"/>
            <SuccessPopup isOpen={e.showSuccess && !reserveOpen} isReturnFlow={e.trip?.trip_type === 'ReturnBooking'} trip={e.trip} onDone={() => navigate('/')}/>
            <ReserveRideModal key={reserveKey} isOpen={reserveOpen} onClose={() => setReserveOpen(false)}
                              onDone={() => setReserveOpen(false)} e={e} missing={missingIdentity}
                              skipCard={hasSavedCards && !!e.selectedCardId}
                              onTerms={() => setShowPoliciesPopup(true)}/>
            <PoliciesPopup isOpen={showPoliciesPopup} onClose={() => setShowPoliciesPopup(false)} trip={e.trip} fromCalculatedPolicies/>
            {/* Same shell as the booking flow's prebookCompare step: header, the shared detail, and
                the perks bar as the footer — "More" goes on to the perks. */}
            <Modal isOpen={showCompareStep} onClose={() => setShowCompareStep(false)}
                   footer={
                       <div className="prebook-compare">
                           <button type="button" className="pc-more-bar"
                                   onClick={() => { setShowCompareStep(false); setShowPerksModal(true) }}>
                               <span className="pc-more-left">
                                   <img src={img('logo-latest.svg')} alt="hopn"/>
                                   Exclusive Airport Pickup Perks
                               </span>
                               <span className="pc-more-link">More <img src={img('arrow-right.svg')} alt=""/></span>
                           </button>
                       </div>
                   }>
                <ModalHeader title="" onClose={() => setShowCompareStep(false)}/>
                <div className="modalPages-spacing padding-bottom-30">
                    <PrebookCompareDetail pickup={pickupCompact} dropoff={dropoffCompact} price={price} sharePrice={sharePrice}/>
                </div>
            </Modal>
            <AirportPerksModal isOpen={showPerksModal} onClose={() => setShowPerksModal(false)} trip={e.trip} fromCalculatedPolicies/>

            <Modal isOpen={showMoreFaq} onClose={() => setShowMoreFaq(false)}>
                <ModalHeader title="Popular questions" onClose={() => setShowMoreFaq(false)}/>
                <div className="hopn-ride-latest">
                    <div className="hopn-ride-faq">
                        <FaqAccordionList items={faqs.slice(FAQ_INLINE_COUNT)}/>
                    </div>
                </div>
            </Modal>
        </div>
    )
}
